import { useReactTable } from '@tanstack/react-table'
import * as React from 'react'

/**
 * The fleet's provider, with the export channel left out.
 *
 * `getExportData` / `exportColumns` / `exportFilename` are the three fields the
 * fleet's copy carries for `DataTableExportButton`, and `export/` is one of the
 * directories this console did not take (design note §6). Keeping the fields
 * with nothing to fill them would be a context that advertises a feature no
 * component here reads — the empty set again. They come back with the button.
 */
interface DataTableContextValue<RecordType> {
  table: ReturnType<typeof useReactTable<RecordType>>
  isLoading?: boolean
  /** Error from data fetching */
  error?: Error | null
  onRowClick?: (row: RecordType) => void
  /** Current global search value */
  searchValue?: string
  /** Handler to update search value */
  onSearchChange?: (value: string) => void
  /** Placeholder for search input */
  searchPlaceholder?: string
  /** Whether global search is enabled */
  searchEnabled?: boolean
}

const DataTableContext = React.createContext<DataTableContextValue<any> | undefined>(undefined)

export function DataTableProvider<RecordType>({
  table,
  isLoading,
  error,
  onRowClick,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  searchEnabled,
  children,
}: React.PropsWithChildren<{
  table: ReturnType<typeof useReactTable<RecordType>>
  isLoading?: boolean
  error?: Error | null
  onRowClick?: (row: RecordType) => void
  searchValue?: string
  onSearchChange?: (value: string) => void
  searchPlaceholder?: string
  searchEnabled?: boolean
}>) {
  return (
    <DataTableContext.Provider
      value={{
        table,
        isLoading,
        error,
        onRowClick,
        searchValue,
        onSearchChange,
        searchPlaceholder,
        searchEnabled,
      }}
    >
      {children}
    </DataTableContext.Provider>
  )
}

export function useDataTable<RecordType>() {
  const context = React.useContext(DataTableContext)
  if (!context) {
    throw new Error('useDataTable must be used within a DataTableProvider')
  }
  return context as DataTableContextValue<RecordType>
}
